<template>
  <p-child
    namespace="gs-address-validation"
    :class="{ 'gs-address-validation': true, 'p-form-element': true, invalid: isValid === false }"
  >
    <div class="gs-address-validation-wrapper">
      <slot></slot>
    </div>
    <p-form-element-messages
      :errors="isValid === false ? $t('addressValidation.addressDoesNotExist') : undefined"
    ></p-form-element-messages>
    <div
      ref="popover"
      v-click-outside="onClickOutside"
      class="gs-address-validation-popover elevation-3"
      popover="manual"
      @toggle="onPopoverToggle"
    >
      <p-surface v-if="isPopoverOpen && addresses" role="listbox">
        <p-list-group>
          <p-list>
            <div v-for="(item, index) in addresses" :key="index" class="p-select-list-entry">
              <p-list-entry
                role="option"
                :title="item.caption"
                :class="{ 'p-select-item': true, active: selectedIndex === index }"
                @click="fillAddress(item)"
              >
                <div class="list-item-content mx-2">
                  {{ item.caption }}
                </div>
              </p-list-entry>
            </div>
          </p-list>
        </p-list-group>
      </p-surface>
    </div>
  </p-child>
</template>

<script lang="ts">
  import Vue, { PropType } from 'vue';
  import ValidateAddressRequestModel from '@/src/services/v2/model/validate-address-request-model';
  import AddressModel from '../../services/v2/model/address-model';

  type AddressValidator = {
    onFocus: (element: Element, field: FieldName | undefined) => void;
    onBlur: (element: Element, field: FieldName | undefined) => void;
    onKeyDown: (event: KeyboardEvent, field: FieldName | undefined) => void;
  };

  type ReadonlyFields = {
    countryIso2?: boolean;
    street?: boolean;
    houseNumber?: boolean;
    postalCode?: boolean;
    postalCodeAddition?: boolean;
    city?: boolean;
  };

  type FieldName = keyof ReadonlyFields;

  const fields: FieldName[] = ['countryIso2', 'street', 'houseNumber', 'postalCode', 'postalCodeAddition', 'city'];

  export default Vue.extend({
    provide(): { addressValidator: AddressValidator } {
      // Used by gs-address-validation-directive.ts
      return {
        addressValidator: {
          onFocus: this.onActivatorFocus,
          onBlur: this.onActivatorBlur,
          onKeyDown: this.onActivatorKeyDown,
        },
      };
    },
    props: {
      countryIso2: { type: String, default: undefined },
      street: { type: String, default: undefined },
      houseNumber: { type: String, default: undefined },
      postalCode: { type: String, default: undefined },
      postalCodeAddition: { type: String, default: undefined },
      city: { type: String, default: undefined },
      readonly: { type: [Object, Boolean] as PropType<ReadonlyFields | boolean>, default: false },
      validAddressRequired: { type: Boolean, default: false },
      warnIfInvalidAddress: { type: Boolean, default: false },
      anchorElementSelector: { type: String, default: '.p-text-field' },
    },
    data: () => ({
      isPopoverOpen: false,
      addresses: [] as AddressModel[],
      isCountrySupported: undefined as boolean | undefined,
      activator: undefined as Element | undefined,
      selectedIndex: undefined as number | undefined,
      lastFilledAddress: undefined as AddressModel | undefined,
      isJustFilledAddress: false,
      isValid: undefined as boolean | undefined,
    }),
    computed: {
      isEnabled() {
        return this.$config.values['addressvalidation-isenabled'] ?? true;
      },
      minSearchLengths() {
        const minLengths = this.$config.values['addressvalidation-minsearchlength'];
        return {
          countryIso2: minLengths?.countryIso2 ?? 0,
          street: minLengths?.street ?? 3,
          houseNumber: minLengths?.houseNumber ?? 0,
          postalCode: minLengths?.postalCode ?? 2,
          postalCodeAddition: minLengths?.postalCodeAddition ?? 0,
          city: minLengths?.city ?? 3,
        };
      },
      hasEnoughInfoToSearch() {
        // At least one field needs to be filled with an input of adequate length
        const enoughInfoToSearch = fields.some((field) => {
          const minLength = this.minSearchLengths[field];
          return minLength > 0 && this[field]?.length >= minLength;
        });
        return enoughInfoToSearch;
      },
    },
    watch: {
      countryIso2() {
        this.search();
      },
      street() {
        this.search();
      },
      houseNumber() {
        this.search();
      },
      postalCode() {
        this.updatePostalCodeAddition();
        this.search();
      },
      postalCodeAddition() {
        this.search();
      },
      city() {
        this.updatePostalCodeAddition();
        this.search();
      },
    },
    methods: {
      // This function can also be called from the parent component
      async validate(hideErrorIfIncomplete: boolean = false): Promise<{ isValid: boolean, errors: string[] }> {
        if (!this.isEnabled) {
          this.isValid = undefined;
          return this.successValidationResult();
        }

        if (!(this.validAddressRequired || this.warnIfInvalidAddress)) {
          this.isValid = undefined;
          return this.successValidationResult();
        }

        const fieldsForValidation = fields.filter((field) => field !== 'postalCodeAddition');
        const allFieldsFilled = fieldsForValidation.every((field) => this[field]);
        if (!allFieldsFilled) {
          if (!hideErrorIfIncomplete) {
            this.isValid = false;
          }
          return this.failedValidationResult();
        }

        try {
          // Ensure addresses are up to date
          await this.sendRequest();
        } catch (error) {
          this.$log.error(error);
          this.isValid = undefined;
          return this.successValidationResult();
        }

        if (!this.isCountrySupported) {
          this.isValid = undefined;
          return this.successValidationResult();
        }

        const valid = this.addresses.some((address) => {
          return fieldsForValidation.every((field) => this[field]?.toLowerCase() === address[field]?.toLowerCase());
        });
        this.isValid = valid;

        return valid ? this.successValidationResult() : this.failedValidationResult();
      },
      successValidationResult() {
        return { isValid: true, errors: [] };
      },
      failedValidationResult() {
        if (this.validAddressRequired) {
          // Only when validAddressRequired is set, should the component block the submitting of the address
          return { isValid: false, errors: [this.$t('addressValidation.addressDoesNotExist')] };
        }
        return this.successValidationResult();
      },
      isFieldReadonly(field: FieldName | undefined) {
        if (typeof this.readonly === 'boolean') {
          return this.readonly;
        }
        if (typeof this.readonly === 'object' && this.readonly != null && field) {
          if (field === 'postalCodeAddition') {
            return this.readonly.postalCodeAddition ?? this.readonly.postalCode;
          }
          return this.readonly[field];
        }
        return false;
      },
      onPopoverToggle(event: Event) {
        this.isPopoverOpen = (event as any).newState === 'open'; // Requires newer typescript version for ToggleEvent type
      },
      hasActivatorFocus() {
        return document.activeElement ? this.activator?.contains(document.activeElement) : false;
      },
      onClickOutside(event: any) {
        const isInside = this.activator?.contains(event.target);
        if (!(isInside || this.hasActivatorFocus())) {
          this.closePopover();
        }
      },
      onActivatorFocus(element: Element, field: FieldName | undefined) {
        if (this.isFieldReadonly(field)) {
          return;
        }
        this.openPopover(element);
      },
      onActivatorBlur() {
        this.validate(true);
      },
      onActivatorKeyDown(event: KeyboardEvent, field: FieldName | undefined) {
        if (this.isFieldReadonly(field)) {
          return;
        }
        if (event.key === 'Tab') {
          this.closePopover();
        } else if (event.key === 'Escape') {
          this.closePopover();
          event.preventDefault();
        } else if (event.key === 'ArrowUp') {
          if (!this.isPopoverOpen) {
            this.openPopover(event.currentTarget as Element);
          }
          this.selectedIndex = Math.max((this.selectedIndex ?? 1) - 1, 0);
          event.preventDefault();
        } else if (event.key === 'ArrowDown') {
          if (!this.isPopoverOpen) {
            this.openPopover(event.currentTarget as Element);
          }
          this.selectedIndex = Math.max(Math.min((this.selectedIndex ?? -1) + 1, this.addresses.length - 1), 0);
          event.preventDefault();
        } else if (event.key === 'Enter') {
          if (this.selectedIndex != null && this.addresses[this.selectedIndex]) {
            this.fillAddress(this.addresses[this.selectedIndex]);
            event.preventDefault();
          }
        } else {
          this.selectedIndex = undefined;
        }
      },
      openPopover(element: Element) {
        if (!this.isEnabled) {
          return;
        }
        if (this.isPopoverOpen) {
          this.closePopover();
        }
        const { popover } = this.$refs;
        const anchor = this.anchorElementSelector ? (element.querySelector(this.anchorElementSelector) ?? element) : element;
        this.activator = element;
        (popover as any).showPopover({ source: anchor }); // Requires newer typescript version for showPopover function
      },
      reopenPopover() {
        if (this.isPopoverOpen || !this.activator || !this.hasActivatorFocus()) {
          return;
        }
        this.openPopover(this.activator);
      },
      closePopover() {
        const { popover } = this.$refs;
        (popover as any).hidePopover(); // Requires newer typescript version for hidePopover function
        this.selectedIndex = undefined;
      },
      search() {
        if (!this.isEnabled || this.isJustFilledAddress) {
          return;
        }
        this.$debounce(async () => {
          if (!this.hasEnoughInfoToSearch) {
            this.addresses = [];
            this.isCountrySupported = undefined;
            return;
          }
          try {
            await this.sendRequest();
            this.reopenPopover();
          } catch (error) {
            this.$log.error(error);
          }
        }, 500, this)();
      },
      async sendRequest() {
        const request = new ValidateAddressRequestModel();
        fields.forEach((field) => { request[field] = this[field]; });
        const result = await Vue.$service.v2.api.address.validateAddress(request);
        this.addresses = result.data.addresses ?? [];
        this.isCountrySupported = result.data.isCountrySupported;
      },
      fillAddress(address: AddressModel) {
        this.closePopover();
        this.addresses = [];
        this.selectedIndex = undefined;
        // Filling an address updates the props which then get picked up by the watchers
        // lastFilledAddress and isJustFilledAddress can be used to filter out these watcher calls
        this.lastFilledAddress = address;
        this.isJustFilledAddress = true;
        this.$nextTick(() => { this.isJustFilledAddress = false; });

        if (!this.isFieldReadonly('countryIso2')) {
          this.$emit('update:countryIso2', address.countryIso2);
        }
        if (!this.isFieldReadonly('street')) {
          this.$emit('update:street', address.street);
        }
        if (!this.isFieldReadonly('houseNumber')) {
          this.$emit('update:houseNumber', address.houseNumber);
        }
        if (!this.isFieldReadonly('postalCode')) {
          this.$emit('update:postalCode', address.postalCode);
        }
        if (!this.isFieldReadonly('postalCodeAddition')) {
          this.$emit('update:postalCodeAddition', address.postalCodeAddition);
        }
        if (!this.isFieldReadonly('city')) {
          this.$emit('update:city', address.city);
        }
      },
      updatePostalCodeAddition() {
        // The user cannot modify the postal code addition because it does not have a text field.
        // When a user manually edits the postal code or city, the postal code addition might be incorrect and should be removed.
        if (this.isFieldReadonly('postalCodeAddition')) {
          return;
        }
        const isKnownAddition = this.postalCode === this.lastFilledAddress?.postalCode && this.city === this.lastFilledAddress?.city;
        const addition = isKnownAddition ? this.lastFilledAddress?.postalCodeAddition : undefined;
        if (this.postalCodeAddition !== addition) {
          this.$emit('update:postalCodeAddition', addition);
        }
      },
    },
  });
</script>
